
import {
    Vue,
    Component,
    Prop,
    Watch
} from 'vue-property-decorator'
import {
    ApplicationDocument,
    Classifier,
    CourierDelivery,
    ValidationError
} from '@/commons/types'
import { ApplicationUtils } from '@/commons/utils/application'
import {
    ApplicationDeliveryType,
    validatorIds
} from '@/commons/constants/application'
import { CourierDeliveryEntity } from '@/store/entities/application'
import InputWithLabel from '@/commons/components/core/InputWithLabel.vue'
import { translateValidationError } from '@/commons/configuration/vee.validate'
import $ from 'jquery'
import Checkbox from '@/commons/components/core/Checkbox.vue'
import { applicantStore, classifiersStore } from '@/store'
import { CourierService } from '@/store/services/courier.service'
import { ENVIRONMENT } from '@/commons/configuration'
import SelectWithSearch from '@/commons/components/core/SelectWithSearch.vue'
import { PlaceOfIssueUtils } from '@/commons/utils/place.of.issue'
import { ClassifiersUtils } from '@/commons/utils/classifiers'
import { ProviderInstance } from 'vee-validate/dist/types/types'

@Component({
    components: { SelectWithSearch, Checkbox, InputWithLabel }
})
export default class CourierDetails extends Vue {
    showCourierDeliveryPrice = false
    showCourierDeliveryLoading = false
    showDeliveryPriceError = false
    courierPrice: string | null = ''
    courierPriceRequestTimeoutId: number | null = null

    @Prop() documentType!: string
    @Prop() citizenPassportCourierCountryId!: string
    @Prop() citizenPassportCourierCityId!: string
    @Prop() citizenPassportCourierPostalCodeId!: string

    domValidatorIds = validatorIds

    get applicationDocument (): ApplicationDocument {
        return ApplicationUtils.getDocument(this.documentType)
    }

    get isCourierRetrieval (): boolean {
        return this.applicationDocument.delivery.type === ApplicationDeliveryType.COURIER_RETRIEVAL
    }

    get courierDelivery (): CourierDelivery {
        if (!this.applicationDocument.delivery.courier) {
            this.applicationDocument.delivery.courier = new CourierDeliveryEntity()
        }
        return this.applicationDocument.delivery.courier
    }

    get contactAddressCountry (): string | null {
        return applicantStore.application.contact.address.country
    }

    get courierDeliveryCountry (): string | null | undefined {
        return this.applicationDocument.delivery.location
    }

    get courierDeliveryCity (): string | null | undefined {
        return this.courierDelivery.city
    }

    get courierDeliveryPostalCode (): string | null | undefined {
        return this.courierDelivery.postalCode
    }

    get courierPlaceOfIssues (): Array<Classifier> {
        return classifiersStore.countries
    }

    get filteredCourierPlaceOfIssues (): Array<Classifier> {
        return PlaceOfIssueUtils.filteredPlaceOfIssues(false, this.courierPlaceOfIssues)
    }

    get selectedCourierPlaceOfIssue (): Classifier | undefined {
        return ClassifiersUtils.findClassifier(this.filteredCourierPlaceOfIssues, this.applicationDocument.delivery.location)
    }

    get labelTranslationKey (): string {
        return 'personContacts.country'
    }

    isForbiddenCourierCountry (location: string | null): boolean {
        return this.isCourierRetrieval && ENVIRONMENT.forbiddenCourierCountries.includes(location)
    }

    blockProceedingWhileLoading (): void {
        applicantStore.isApplicationProceedBlocked = true
    }

    setCourierDeliveryCountry (country: string | null): void {
        this.applicationDocument.delivery.location = country
    }

    setCourierDeliveryCity (city: string | null): void {
        this.courierDelivery.city = city
    }

    setCourierDeliveryPostalCode (postalCode: string | null): void {
        this.courierDelivery.postalCode = postalCode
    }

    validatePlaceOfIssue (): void {
        (this.$refs.placeOfIssueValidator as ProviderInstance).validate()
    }

    setCourierDeliveryAddressSameAsAddress (value: boolean): void {
        if (value) {
            this.setCourierDeliveryCountry(applicantStore.application.contact.address.country)
            this.setCourierDeliveryCity(applicantStore.application.contact.address.city)
            this.setCourierDeliveryPostalCode(applicantStore.application.contact.address.postalCode)
        }
    }

    showWarningModal (): void {
        const courierWarningModal = $('#courierWarningModal-' + this.documentType)
        if (courierWarningModal && courierWarningModal.modal) {
            courierWarningModal.modal('show')
        }
    }

    showCourierPriceNotice (): void {
        this.showCourierDeliveryPrice = true
    }

    showCourierPriceLoading (): void {
        this.showCourierDeliveryLoading = true
    }

    showCourierPriceError (): void {
        this.showDeliveryPriceError = true
    }

    hideCourierPriceNotice (): void {
        this.showCourierDeliveryPrice = false
    }

    hideCourierPriceLoading (): void {
        this.showCourierDeliveryLoading = false
    }

    hideCourierPriceError (): void {
        this.showDeliveryPriceError = false
    }

    allowApplicationProceeding (): void {
        applicantStore.isApplicationProceedBlocked = false
    }

    async getCourierPreliminaryPrice (): Promise<void> {
        this.hideCourierPriceError()
        this.hideCourierPriceNotice()
        this.showCourierPriceLoading()
        this.blockProceedingWhileLoading()
        try {
            this.courierPrice = await CourierService.fetchCourierPrice(this.applicationDocument.delivery.location, this.courierDelivery.city, this.courierDelivery.postalCode)
            this.hideCourierPriceLoading()
            this.allowApplicationProceeding()
            this.showCourierPriceNotice()
        } catch {
            this.hideCourierPriceLoading()
            this.allowApplicationProceeding()
            this.showCourierPriceError()
        }
    }

    @Watch('courierDeliveryCountry', { immediate: true })
    @Watch('courierDeliveryCity', { immediate: true })
    @Watch('courierDeliveryPostalCode', { immediate: true })
    onAddressReadyMakeRequest (): void {
        if (this.courierPriceRequestTimeoutId != null) {
            clearTimeout(this.courierPriceRequestTimeoutId)
        }
        if (this.courierDeliveryCountry !== undefined && this.isForbiddenCourierCountry(this.courierDeliveryCountry)) {
            this.showWarningModal()
        }

        if (this.isAllFilled) {
            this.courierPriceRequestTimeoutId = setTimeout(() => {
                this.getCourierPreliminaryPrice()
            }, 6000)
        } else {
            this.hideCourierPriceNotice()
        }
    }

    get isAllFilled (): boolean {
        const courier = this.applicationDocument.delivery.courier
        return !!(courier && courier.city != null && courier.city.length >= 3 && courier.postalCode != null && courier.postalCode.length >= 3 && this.applicationDocument.delivery.location)
    }

    translateError (validationError: ValidationError): string {
        return translateValidationError(validationError)
    }
}
